/* eslint-disable react/prop-types */
import React, { useLayoutEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import Icon from '../Icon';
import { tokens, orange, inkA } from '../tokens';
import useIsMobile from '../useIsMobile';

const range = (start, end) =>
  Array.from({ length: end - start + 1 }, (_, i) => start + i);

// Build the list of page slots to render: page 1 and the last page are always
// shown, plus a fixed-width inner window (2*siblings + 3 pages) that slides
// with `current` but never runs past either edge. An inner slot that is not
// adjacent to page 1 / the last page collapses to a 'left-dots'/'right-dots'
// sentinel, so a gap is only ever shown for two or more hidden pages and the
// visible count stays fixed (2*siblings + 5) regardless of `current`.
export const buildPages = (current, pageCount, siblings) => {
  const slots = siblings * 2 + 5;
  if (pageCount <= slots) return range(1, pageCount);

  const start = Math.max(
    Math.min(current - siblings - 1, pageCount - slots + 2),
    2
  );
  const inner = range(start, start + slots - 3);
  if (inner[0] > 2) inner[0] = 'left-dots';
  if (inner[inner.length - 1] < pageCount - 1) {
    inner[inner.length - 1] = 'right-dots';
  }
  return [1, ...inner, pageCount];
};

// Metrics for the two densities. Mobile buttons are 48px tall/wide for a
// comfortable tap target (matching SearchInput/AddButton) without inflating
// the type. Widths are used both for styling and to predict row width.
const DESKTOP = { size: 34, icon: 16, font: 13.5, pad: 7, dots: 14, gap: 8 };
const MOBILE = { size: 48, icon: 22, font: 14, pad: 10, dots: 20, gap: 6 };

// Widest possible row (in px) for a given siblings count, over every value of
// `current`, so the layout picked for a table never flips while paging through
// it. Worst case is a near-edge window: all-but-one slots are buttons and one
// is an ellipsis.
export const rowWidth = (siblings, pageCount, m) => {
  const slots = Math.min(pageCount, siblings * 2 + 5);
  const windowed = pageCount > slots;
  const dots = windowed ? 1 : 0;
  const buttons = slots - dots;
  const digits = String(pageCount).length;
  const btnW = Math.max(m.size, Math.ceil(digits * m.font * 0.62) + m.pad * 2);
  const items = buttons + dots + 2; // + prev/next chevrons
  return buttons * btnW + 2 * m.size + dots * m.dots + (items - 1) * m.gap;
};

// Pick the widest windowing that fits in `width`; null means even the
// narrowest numbered row overflows and the compact "3 of 20" pager is used.
// An unmeasured container (width 0: first paint, SSR, jsdom) falls back to
// the breakpoint default.
export const pickSiblings = (width, pageCount, m, fallback) => {
  if (!width) return fallback;
  const fits = [2, 1, 0].find((s) => rowWidth(s, pageCount, m) <= width);
  return fits === undefined ? null : fits;
};

// Container width, kept current via ResizeObserver (window resize as a
// fallback where it is unavailable).
const useContainerWidth = (ref) => {
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setWidth(el.getBoundingClientRect().width);
    measure();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [ref]);
  return width;
};

const PageButton = ({
  theme,
  t,
  m,
  active,
  disabled,
  onClick,
  label,
  children,
}) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    aria-label={label}
    aria-current={active ? 'page' : undefined}
    style={{
      minWidth: m.size,
      height: m.size,
      padding: `0 ${m.pad}px`,
      borderRadius: 8,
      cursor: disabled ? 'default' : 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: `${m.font}px`,
      fontWeight: 700,
      fontVariantNumeric: 'tabular-nums',
      background: active ? orange(0.1) : 'transparent',
      border: '1px solid ' + (active ? t.primary : inkA(theme, 0.12)),
      color: active
        ? t.primary
        : disabled
        ? inkA(theme, 0.25)
        : inkA(theme, 0.72),
      transition: 'background 120ms ease, border-color 120ms ease',
    }}
  >
    {children}
  </button>
);

const Dots = ({ theme, m }) => (
  <span
    aria-hidden="true"
    style={{
      minWidth: m.dots,
      height: m.size,
      display: 'inline-flex',
      alignItems: 'flex-end',
      justifyContent: 'center',
      paddingBottom: 4,
      fontSize: `${m.font}px`,
      fontWeight: 700,
      color: inkA(theme, 0.4),
      userSelect: 'none',
    }}
  >
    …
  </span>
);

const clamp = (n, lo, hi) => Math.min(Math.max(n, lo), hi);

// Numbered pager. Controlled via `page`/`onChange`, or self-managed if omitted.
const Pagination = ({ page, pageCount = 1, onChange }) => {
  const theme = useTheme();
  const t = tokens(theme);
  const isMobile = useIsMobile();
  const m = isMobile ? MOBILE : DESKTOP;
  const ref = useRef(null);
  const width = useContainerWidth(ref);
  const [internal, setInternal] = useState(1);

  // Coerce (query-string callers pass strings) and clamp: a stale `page`
  // after a refetch shrinks `pageCount` should still show a sane pager.
  const count = Math.max(1, Math.floor(Number(pageCount)) || 1);
  const requested = Number(page != null ? page : internal);
  const current = clamp(Math.floor(requested) || 1, 1, count);
  const goTo = (n) => {
    if (onChange) onChange(n);
    if (page == null) setInternal(n);
  };

  const siblings = pickSiblings(width, count, m, isMobile ? 0 : 2);
  const pages = siblings === null ? [] : buildPages(current, count, siblings);

  return (
    <nav
      ref={ref}
      aria-label="Pagination"
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: m.gap,
        padding: '18px 0 20px',
      }}
    >
      <PageButton
        theme={theme}
        t={t}
        m={m}
        label="Previous page"
        disabled={current === 1}
        onClick={() => goTo(current - 1)}
      >
        <Icon
          name="chevron-left"
          size={m.icon}
          color={current === 1 ? inkA(theme, 0.25) : inkA(theme, 0.62)}
        />
      </PageButton>
      {siblings === null ? (
        <span
          style={{
            padding: `0 ${m.pad}px`,
            fontSize: `${m.font}px`,
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
            color: inkA(theme, 0.72),
            whiteSpace: 'nowrap',
          }}
        >
          {current} of {count}
        </span>
      ) : (
        pages.map((n) =>
          typeof n === 'number' ? (
            <PageButton
              key={n}
              theme={theme}
              t={t}
              m={m}
              active={current === n}
              onClick={() => goTo(n)}
            >
              {n}
            </PageButton>
          ) : (
            <Dots key={n} theme={theme} m={m} />
          )
        )
      )}
      <PageButton
        theme={theme}
        t={t}
        m={m}
        label="Next page"
        disabled={current === count}
        onClick={() => goTo(current + 1)}
      >
        <Icon
          name="chevron-right"
          size={m.icon}
          color={current === count ? inkA(theme, 0.25) : inkA(theme, 0.62)}
        />
      </PageButton>
    </nav>
  );
};

export default Pagination;
